import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { EMPTY, of } from 'rxjs';
import { catchError, expand, filter, map, reduce, tap } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { PaginatedHttpDTO } from '../dtos/http-dto';
import { User } from '../domain/user-model';

export interface Permission {
  id: string;
  group_id: string;
  name: string;
  slug: string;
}

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  permissions = signal<Permission[]>([]);
  isLoaded = signal(false);
  private superuserBypass = signal(false);
  private syncVersion = 0;
  private permissionSlugs = computed(
    () => new Set(this.permissions().map((p) => this.normalizePermissionSlug(p.slug))),
  );

  private normalizePermissionSlug(slug: string): string {
    return slug.replace('::', ':').trim().toLowerCase();
  }

  private isSuperuser(user: User): boolean {
    return (user.superuser ?? false) || (user.role?.superuser ?? false);
  }

  syncFromUser(user: User, isCurrentSession: () => boolean = () => true) {
    if (!isCurrentSession()) return EMPTY;
    const version = ++this.syncVersion;
    if (this.isSuperuser(user)) {
      this.permissions.set([]);
      this.superuserBypass.set(true);
      this.isLoaded.set(true);
      return of([]);
    }

    const roleId = user.role?.id;

    if (!roleId) {
      this.permissions.set([]);
      this.superuserBypass.set(false);
      this.isLoaded.set(true);
      return of([]);
    }

    this.superuserBypass.set(false);
    this.isLoaded.set(false);

    const params = new HttpParams().set('role_id', roleId).set('items_per_page', '50');

    return this.http
      .get<PaginatedHttpDTO<Permission[]>>(`${this.apiUrl}/permissions`, {
        params: params.set('page', '1'),
      })
      .pipe(
        expand((response) => {
          if (
            !isCurrentSession() ||
            version !== this.syncVersion ||
            response.metadata.page >= response.metadata.total_pages
          ) {
            return EMPTY;
          }
          return this.http.get<PaginatedHttpDTO<Permission[]>>(`${this.apiUrl}/permissions`, {
            params: params.set('page', String(response.metadata.page + 1)),
          });
        }),
        map((response) => response.data),
        reduce((permissions, page) => {
          permissions.push(...page);
          return permissions;
        }, [] as Permission[]),
        filter(isCurrentSession),
        tap((permissions) => {
          if (version !== this.syncVersion) return;
          this.permissions.set(permissions);
          this.isLoaded.set(true);
        }),
        catchError((error) => {
          if (!isCurrentSession()) return EMPTY;
          console.error('Erro ao carregar permissoes do usuario:', error);
          if (version === this.syncVersion) {
            this.permissions.set([]);
            this.isLoaded.set(true);
          }
          return of([]);
        }),
      );
  }

  hasPermission(requiredPermission?: string): boolean {
    if (!requiredPermission) return true;
    if (this.superuserBypass()) return true;
    if (!this.isLoaded()) return false;

    const normalizedRequiredPermission = this.normalizePermissionSlug(requiredPermission);
    const slugs = this.permissionSlugs();

    // Permite configurar wildcard por recurso (ex.: products::*).
    if (normalizedRequiredPermission.endsWith('*')) {
      const prefix = normalizedRequiredPermission.slice(0, -1);
      for (const slug of slugs) {
        if (slug.startsWith(prefix)) {
          return true;
        }
      }
      return false;
    }

    return slugs.has(normalizedRequiredPermission);
  }

  reset() {
    this.syncVersion++;
    this.permissions.set([]);
    this.isLoaded.set(false);
    this.superuserBypass.set(false);
  }
}
